/**
 * Priced entries (0.9.14, `Funds\Modules\EntriesModule`). The donor says how many — in the administrator's own
 * words, "How many people?" — and that many entry blocks open, each asking the fund's own questions once. The
 * gift is priced by the server from the entries; this only collects them and shows the server's figure.
 *
 * Nothing here is ever written to the draft: an entry describes another person (a name, a prayer), the same
 * reason the fund's own answers are kept out of `localStorage` (`Wizard.collectFieldAnswers()`).
 */

import { digitsOnly } from './digits.js';

const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );

export class EntriesUi {
	/**
	 * @param {Object} wizard The checkout wizard (its `buildField()`, `customFields`, formatting and summary).
	 */
	constructor( wizard ) {
		this.wizard = wizard;
		this.schema = wizard.form.entries || {};
		this.fields = ( wizard.form.fields || [] ).filter( ( field ) => field.per_entry );
		this.blocks = [];
		this.min = Math.max( 1, Number( this.schema.min_entries ) || 1 );
		this.max = Math.max( this.min, Number( this.schema.max_entries ) || this.min );
		this.minimumNote = null;
	}

	/**
	 * The section: the "how many" question, the entry blocks, the price and, when allowed, the donor's own amount.
	 *
	 * @return {HTMLElement}
	 */
	build() {
		const section = document.createElement( 'div' );
		const id = `${ this.wizard.idPrefix }-entries-count`;

		section.className = 'anudan-checkout__entries';

		const countField = document.createElement( 'div' );

		countField.className = 'anudan-checkout__field anudan-checkout__entries-count';
		countField.setAttribute( 'data-anudan-field', 'entries' );

		const label = document.createElement( 'label' );
		const perEntry = 'per_unit' !== this.schema.pricing;

		label.className = 'anudan-checkout__field-label';
		label.htmlFor = id;
		// C6, D1 (0.9.40): a price per entry is said with the question, "How many names? $25.00 each".
		label.textContent = perEntry ? `${ this.question() } ${ this.priceText() }` : this.question();

		const price = document.createElement( 'p' );

		price.className = 'anudan-checkout__field-help';
		price.id = `${ id }-price`;
		price.textContent = this.priceText();
		price.hidden = perEntry;

		this.countInput = document.createElement( 'input' );
		this.countInput.type = 'number';
		this.countInput.inputMode = 'numeric';
		this.countInput.id = id;
		this.countInput.className = 'anudan-checkout__field-input anudan-checkout__entries-input';
		this.countInput.min = String( this.min );
		this.countInput.max = String( this.max );
		this.countInput.step = '1';
		this.countInput.value = String( this.min );
		this.countInput.required = true;
		// R3-3 (rc4): digits only as it is typed or pasted.
		digitsOnly( this.countInput );
		this.countInput.setAttribute( 'aria-describedby', price.id );
		this.countInput.addEventListener( 'input', () => this.setCount( this.countInput.value ) );

		// Q1 (0.9.40): the count between a minus and a plus, as the design draws it; typing a number works as before.
		const stepper = document.createElement( 'div' );
		const step = ( by, text, name ) => {
			const button = document.createElement( 'button' );

			button.type = 'button';
			button.className = 'anudan-checkout__step-button';
			button.textContent = text;
			button.setAttribute( 'aria-label', name );
			button.addEventListener( 'click', () => {
				const now = parseInt( this.countInput.value, 10 ) || 0;
				const next = Math.min( this.max, Math.max( this.min, now + by ) );

				this.countInput.value = String( next );
				this.setCount( next );
			} );

			return button;
		};

		const head = document.createElement( 'div' );

		head.className = 'anudan-checkout__entries-head';
		stepper.className = 'anudan-checkout__stepper';
		stepper.appendChild( step( -1, '−', __( 'One fewer' ) ) );
		stepper.appendChild( this.countInput );
		stepper.appendChild( step( 1, '+', __( 'One more' ) ) );
		head.appendChild( label );
		head.appendChild( stepper );
		countField.appendChild( head );
		countField.appendChild( price );
		section.appendChild( countField );

		this.list = document.createElement( 'div' );
		this.list.className = 'anudan-checkout__entries-list';
		section.appendChild( this.list );

		// Q1 (0.9.40): with a price per unit, each entry has its own count, and entries are added and removed one by one.
		if ( ! perEntry ) {
			countField.hidden = true;
			this.addButton = document.createElement( 'button' );
			this.addButton.type = 'button';
			this.addButton.className = 'anudan-checkout__button anudan-checkout__button--outline anudan-checkout__entries-add';
			const plus = document.createElement( 'span' );

			plus.setAttribute( 'aria-hidden', 'true' );
			plus.textContent = '+ ';
			this.addButton.appendChild( plus );
			/* translators: %s: what one entry is called, e.g. "entry". */
			this.addButton.appendChild( document.createTextNode( __( 'Add another %s' ).replace( '%s', String( this.schema.entry_label || __( 'entry' ) ).toLowerCase() ) ) );
			this.addButton.addEventListener( 'click', () => {
				this.setCount( this.blocks.length + 1 );
				this.countInput.value = String( this.blocks.length );

				const last = this.blocks[ this.blocks.length - 1 ];
				const first = last ? last.element.querySelector( 'input, select, textarea' ) : null;

				if ( first ) {
					first.focus();
				}
			} );
			section.appendChild( this.addButton );
		}

		this.minimumNote = document.createElement( 'p' );
		this.minimumNote.className = 'anudan-checkout__entries-total';
		this.minimumNote.setAttribute( 'aria-live', 'polite' );
		section.appendChild( this.minimumNote );

		if ( this.schema.allow_more ) {
			section.appendChild( this.buildOwnAmount() );
		}

		this.setCount( this.min );

		return section;
	}

	/**
	 * "$175.00 for each name" or "$2.00 for each one counted".
	 *
	 * @return {string}
	 */
	priceText() {
		const amount = this.wizard.formatMinorUnits( Number( this.schema.price ) || 0 );
		const unit = this.fields.find( ( field ) => field.key === this.schema.unit_field );

		if ( 'per_unit' === this.schema.pricing ) {
			/* translators: 1: a price, 2: the number question's label. */
			return __( '%1$s for each — %2$s' ).replace( '%1$s', amount ).replace( '%2$s', unit ? unit.label : __( 'count' ) );
		}

		/* translators: %s: a price, e.g. "$25.00". */
		return __( '%s each' ).replace( '%s', amount );
	}

	/**
	 * The "how many" question: the fund's own words, or "How many names?" from what one entry is called.
	 *
	 * @return {string}
	 */
	question() {
		if ( this.schema.question ) {
			return this.schema.question;
		}

		/* translators: %s: what several entries are called, e.g. "names". */
		return __( 'How many %s?' ).replace( '%s', this.plural().toLowerCase() );
	}

	/**
	 * What several entries are called: "Names" for entries called "Name".
	 *
	 * @return {string}
	 */
	plural() {
		const one = String( this.schema.entry_label || __( 'Entry' ) );

		return /s$/i.test( one ) ? one : ( /y$/i.test( one ) ? `${ one.slice( 0, -1 ) }ies` : `${ one }s` );
	}

	/**
	 * Q1 (0.9.40): the gift step's part is titled by what the entries are ("Names").
	 *
	 * @return {string}
	 */
	sectionTitle() {
		return this.plural();
	}

	/**
	 * Q1 (0.9.40): the line in the "Your gift" card, "3 names × $25.00", for a price per entry; '' otherwise.
	 *
	 * @return {string}
	 */
	summaryLine() {
		if ( 'per_unit' === this.schema.pricing ) {
			return '';
		}

		/* translators: 1: how many, 2: what they are called, 3: the price of one. */
		return __( '%1$s %2$s × %3$s' )
			.replace( '%1$s', String( this.count() ) )
			.replace( '%2$s', 1 === this.count() ? String( this.schema.entry_label || __( 'entry' ) ).toLowerCase() : this.plural().toLowerCase() )
			.replace( '%3$s', this.wizard.formatMinorUnits( Number( this.schema.price ) || 0 ) );
	}

	/**
	 * Q1 (0.9.40): with a price per unit, one line per entry in the "Your gift" card, "Entry 1: 2 shares × $50.00", with its
	 * figure; null otherwise. The figure is only shown: the server prices the gift (the total comes from its quote).
	 *
	 * @return {Array<Array>|null} [ words, minor units ] for each entry.
	 */
	summaryLines() {
		if ( 'per_unit' !== this.schema.pricing ) {
			return null;
		}

		return this.blocks.map( ( block, index ) => [
			`${ this.entryName( index ) }: ${ this.unitWords( this.unitsOf( block ) ) }`,
			this.unitsOf( block ) * ( Number( this.schema.price ) || 0 ),
		] );
	}

	/**
	 * How many units an entry's own count says, never below zero.
	 *
	 * @param {Object} block The entry.
	 * @return {number}
	 */
	unitsOf( block ) {
		const input = block.unitInput;
		const typed = input ? parseInt( input.value, 10 ) : 0;

		return Number.isFinite( typed ) && typed > 0 ? typed : 0;
	}

	/**
	 * "2 shares × $50.00", from the unit question's own label.
	 *
	 * @param {number} units How many.
	 * @return {string}
	 */
	unitWords( units ) {
		const unit = this.fields.find( ( field ) => field.key === this.schema.unit_field );
		// "Shares" stays "shares"; a question worded "How many times" counts "times".
		const many = String( unit ? unit.label : __( 'Units' ) ).toLowerCase().replace( /^how many\s+/, '' ).replace( /\?$/, '' );
		const one = /ies$/.test( many ) ? `${ many.slice( 0, -3 ) }y` : many.replace( /s$/, '' );

		/* translators: 1: how many, 2: what they are called, 3: the price of one. */
		return __( '%1$s %2$s × %3$s' )
			.replace( '%1$s', String( units ) )
			.replace( '%2$s', 1 === units ? one : many )
			.replace( '%3$s', this.wizard.formatMinorUnits( Number( this.schema.price ) || 0 ) );
	}

	/**
	 * "Entry 2".
	 *
	 * @param {number} index The entry, from 0.
	 * @return {string}
	 */
	entryName( index ) {
		return `${ this.schema.entry_label || __( 'Entry' ) } ${ index + 1 }`;
	}

	/**
	 * The donor's own amount, when the fund lets them give more than the price.
	 *
	 * @return {HTMLElement}
	 */
	buildOwnAmount() {
		const wrapper = document.createElement( 'div' );
		const id = `${ this.wizard.idPrefix }-entries-amount`;

		wrapper.className = 'anudan-checkout__field';
		wrapper.setAttribute( 'data-anudan-field', 'amount' );

		const label = document.createElement( 'label' );

		label.className = 'anudan-checkout__field-label';
		label.htmlFor = id;
		label.textContent = __( 'Your gift, if you would like to give more (optional)' );
		wrapper.appendChild( label );

		const input = document.createElement( 'input' );

		input.type = 'text';
		input.inputMode = 'decimal';
		input.id = id;
		input.className = 'anudan-checkout__field-input';
		input.addEventListener( 'input', () => {
			const minor = this.wizard.parseMinorUnits( input.value );

			this.wizard.customAmount = null === minor || minor <= 0 ? null : minor;
			this.wizard.updateSummary();
		} );
		wrapper.appendChild( input );

		// C5 (0.9.40): when what is typed is less than the entries cost, the wizard says it was raised, here under the box.
		const raised = document.createElement( 'p' );

		raised.className = 'anudan-checkout__raised-note';
		raised.setAttribute( 'aria-live', 'polite' );
		raised.hidden = true;
		wrapper.appendChild( raised );
		this.wizard.raisedNote = raised;

		return wrapper;
	}

	/**
	 * Opens or closes entry blocks to match the count typed, within the fund's bounds.
	 *
	 * @param {string|number} value What was typed.
	 * @return {void}
	 */
	setCount( value ) {
		const typed = parseInt( String( value ), 10 );
		const count = Number.isFinite( typed ) ? Math.min( this.max, Math.max( 0, typed ) ) : 0;

		while ( this.blocks.length < count ) {
			this.addBlock();
		}

		while ( this.blocks.length > count ) {
			this.removeBlock( this.blocks[ this.blocks.length - 1 ] );
		}

		this.refreshBlocks();
		this.wizard.updateSummary();
	}

	/**
	 * One entry's block: its number and name, and the fund's questions for it. Its answers are kept under a serial that never
	 * changes, so removing an entry in the middle leaves the others' answers where they are (`collect()` puts them in order).
	 *
	 * @return {void}
	 */
	addBlock() {
		this.serial = ( this.serial || 0 ) + 1;

		const serial = this.serial;
		const element = document.createElement( 'fieldset' );
		const entry = { element, serial, unitInput: null, line: null, legend: null, remove: null };

		element.className = 'anudan-checkout__entry';

		const legend = document.createElement( 'legend' );

		legend.className = 'anudan-checkout__entry-title';
		element.appendChild( legend );
		entry.legend = legend;

		// Q1 (0.9.40): an entry beyond the fund's least number can be removed on its own.
		const remove = document.createElement( 'button' );

		remove.type = 'button';
		remove.className = 'anudan-checkout__entry-remove';
		remove.textContent = __( 'Remove' );
		remove.addEventListener( 'click', () => {
			this.removeBlock( entry );
			this.countInput.value = String( this.blocks.length );
			this.refreshBlocks();
			this.wizard.updateSummary();

			if ( this.addButton ) {
				this.addButton.focus();
			}
		} );
		element.appendChild( remove );
		entry.remove = remove;

		this.fields.forEach( ( field ) => {
			const control = this.wizard.buildField( { ...field, key: `entries.${ serial }.${ field.key }` } );

			if ( field.key === this.schema.unit_field ) {
				this.unitControl( entry, control, field );
			}

			element.appendChild( control );
		} );

		if ( entry.unitInput ) {
			entry.line = document.createElement( 'p' );
			entry.line.className = 'anudan-checkout__entry-line';
			element.appendChild( entry.line );
		}

		this.list.appendChild( element );
		this.blocks.push( entry );

		// The block slides in on the next frame; a donor who prefers less motion sees it at once (CSS).
		window.requestAnimationFrame( () => element.classList.add( 'is-open' ) );
	}

	/**
	 * Q1 (0.9.40): an entry's unit count, "Shares, $50.00 each", between a minus and a plus; it starts at one.
	 *
	 * @param {Object}      entry   The entry.
	 * @param {HTMLElement} control The question as `Wizard.buildField()` built it.
	 * @param {Object}      field   The question.
	 * @return {void}
	 */
	unitControl( entry, control, field ) {
		const input = control.querySelector( 'input' );
		const label = control.querySelector( '.anudan-checkout__field-label' );

		if ( ! input ) {
			return;
		}

		entry.unitInput = input;
		input.classList.add( 'anudan-checkout__entries-input' );
		input.inputMode = 'numeric';
		input.min = '1';

		if ( '' === input.value ) {
			input.value = '1';
		}

		if ( label ) {
			/* translators: 1: the unit question's label, 2: the price of one, e.g. "$50.00". */
			label.textContent = __( '%1$s, %2$s each' ).replace( '%1$s', field.label ).replace( '%2$s', this.wizard.formatMinorUnits( Number( this.schema.price ) || 0 ) );
		}

		const stepper = document.createElement( 'div' );
		const head = document.createElement( 'div' );
		const step = ( by, text, name ) => {
			const button = document.createElement( 'button' );

			button.type = 'button';
			button.className = 'anudan-checkout__step-button';
			button.textContent = text;
			button.setAttribute( 'aria-label', name );
			button.addEventListener( 'click', () => {
				input.value = String( Math.max( 1, this.unitsOf( entry ) + by ) );
				input.dispatchEvent( new Event( 'input', { bubbles: true } ) );
			} );

			return button;
		};

		stepper.className = 'anudan-checkout__stepper';
		head.className = 'anudan-checkout__entries-head';
		input.replaceWith( stepper );
		stepper.appendChild( step( -1, '−', __( 'One fewer' ) ) );
		stepper.appendChild( input );
		stepper.appendChild( step( 1, '+', __( 'One more' ) ) );

		if ( label ) {
			label.replaceWith( head );
			head.appendChild( label );
		} else {
			control.appendChild( head );
		}

		head.appendChild( stepper );
		input.addEventListener( 'input', () => {
			this.refreshBlocks();
			this.wizard.updateSummary();
		} );
	}

	/**
	 * Takes one entry away, with its answers.
	 *
	 * @param {Object} entry The entry.
	 * @return {void}
	 */
	removeBlock( entry ) {
		const at = this.blocks.indexOf( entry );

		if ( at < 0 ) {
			return;
		}

		this.blocks.splice( at, 1 );
		entry.element.remove();
		Object.keys( this.wizard.customFields )
			.filter( ( key ) => key.startsWith( `entries.${ entry.serial }.` ) )
			.forEach( ( key ) => delete this.wizard.customFields[ key ] );
	}

	/**
	 * Numbers the entries in order, shows Remove only above the fund's least number, and says what each entry's units come to.
	 *
	 * @return {void}
	 */
	refreshBlocks() {
		this.blocks.forEach( ( entry, index ) => {
			entry.legend.textContent = this.entryName( index );
			// With a price per entry the minus button sets the count; Remove is for entries added one by one (a price per unit).
			entry.remove.hidden = 'per_unit' !== this.schema.pricing || this.blocks.length <= this.min;
			entry.remove.setAttribute( 'aria-label', __( 'Remove %s' ).replace( '%s', this.entryName( index ) ) );

			if ( entry.line ) {
				const units = this.unitsOf( entry );

				entry.line.textContent = `${ this.unitWords( units ) } = ${ this.wizard.formatMinorUnits( units * ( Number( this.schema.price ) || 0 ) ) }`;
			}
		} );

		if ( this.addButton ) {
			this.addButton.hidden = this.blocks.length >= this.max;
		}
	}

	/**
	 * How many entries are open.
	 *
	 * @return {number}
	 */
	count() {
		return this.blocks.length;
	}

	/**
	 * Every entry's answers, keyed by the fund's own question keys — what `POST /checkout/intent` receives.
	 *
	 * @return {Object[]}
	 */
	collect() {
		const entries = this.blocks.map( () => ( {} ) );
		const order = this.blocks.map( ( entry ) => entry.serial );

		Object.keys( this.wizard.customFields ).forEach( ( key ) => {
			const match = /^entries\.(\d+)\.(.+)$/.exec( key );
			const at = match ? order.indexOf( Number( match[ 1 ] ) ) : -1;

			if ( at < 0 ) {
				return;
			}

			const value = this.wizard.customFields[ key ]();

			if ( '' === value || false === value || ( Array.isArray( value ) && ! value.length ) ) {
				return;
			}

			entries[ at ][ match[ 2 ] ] = value;
		} );

		return entries;
	}

	/**
	 * What a quote needs to price the gift: the number of entries and, for unit pricing, each unit count —
	 * never a name or a prayer.
	 *
	 * @return {Object[]}
	 */
	quotePayload() {
		const unit = this.schema.unit_field;

		return this.collect().map( ( entry ) => ( 'per_unit' === this.schema.pricing && unit && undefined !== entry[ unit ] ? { [ unit ]: entry[ unit ] } : {} ) );
	}

	/**
	 * Shows the server's minimum for these entries.
	 *
	 * @param {number|null} minorUnits The minimum, from the quote.
	 * @return {void}
	 */
	showMinimum( minorUnits ) {
		if ( ! this.minimumNote ) {
			return;
		}

		this.minimumNote.textContent = 'number' === typeof minorUnits && minorUnits > 0
			? ( this.schema.allow_more ? __( 'At least %s for these entries.' ) : __( '%s for these entries.' ) ).replace( '%s', this.wizard.formatMinorUnits( minorUnits ) )
			: '';
	}
}
